import { format, parseISO } from 'date-fns';

export type FinanceMetricFormat = 'currency' | 'count' | 'ratio' | 'percent';

const currencyFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const countFormatter = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const percentFormatter = new Intl.NumberFormat('en-GB', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export function formatCompareDelta(
  current: number,
  compare: number,
  format: FinanceMetricFormat,
): string | null {
  if (!Number.isFinite(current) || !Number.isFinite(compare)) return null;

  if (format === 'percent') {
    const pp = (current - compare) * 100;
    if (Math.abs(pp) < 0.05) return null;
    const sign = pp > 0 ? '+' : '';
    return `${sign}${pp.toFixed(1)}pp`;
  }

  if (format === 'ratio') {
    if (compare === 0) return current > 0 ? 'New' : null;
    const pct = ((current - compare) / compare) * 100;
    if (Math.abs(pct) < 0.5) return null;
    return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
  }

  if (compare === 0) {
    if (current === 0) return null;
    return current > 0 ? 'New' : null;
  }

  const pct = ((current - compare) / Math.abs(compare)) * 100;
  if (Math.abs(pct) < 0.5) return null;
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

export function formatFinanceValue(
  value: number | null | undefined,
  format: FinanceMetricFormat,
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }

  if (format === 'count') {
    return countFormatter.format(value);
  }

  if (format === 'ratio') {
    return `${value.toFixed(2)}x`;
  }

  if (format === 'percent') {
    return percentFormatter.format(value);
  }

  if (value < 0) {
    return `(${currencyFormatter.format(Math.abs(value))})`;
  }

  return currencyFormatter.format(value);
}

export function granularityLabel(granularity: string): string {
  if (granularity === 'day') return 'Daily view';
  if (granularity === 'year') return 'Yearly view';
  return 'Monthly view';
}

export function formatFinancePeriodLabel(
  period: string,
  granularity?: string,
): string {
  if (!period) return period;

  if (granularity === 'year' || /^\d{4}$/.test(period)) {
    return period.slice(0, 4);
  }

  if (granularity === 'month' || /^\d{4}-\d{2}$/.test(period)) {
    try {
      return format(parseISO(`${period}-01T12:00:00`), 'MMMM yyyy');
    } catch {
      return period;
    }
  }

  try {
    const dateStr = period.length <= 10 ? `${period}T12:00:00` : period;
    return format(parseISO(dateStr), 'd MMMM yyyy');
  } catch {
    return period;
  }
}
